import React from 'react';

import { css } from '@emotion/react';
import { BreadcrumbsState } from '@gumtree/shared/src/model/breadcrumbs.model';
import { Breadcrumbs, Container } from '@gumtree/ui-library/src';
import { PageLayout } from '@gumtree/shared/src/types/srp';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';

import {
    breakpoints,
    colors,
    colorVariables,
    fontSizes,
    gutterSizes,
    mediaQuery,
    zIndexes,
} from '@gumtree/ui-library/src/base/theme';

const hiddenUntilLarge = css`
    ${mediaQuery.until(breakpoints.large)} {
        display: none;
    }
`;

const hiddenUntilMedium = css`
    ${mediaQuery.until(breakpoints.medium)} {
        display: none;
    }
`;

const hiddenFromLarge = css`
    ${mediaQuery.from(breakpoints.large)} {
        display: none;
    }
`;

export const rightFullBleedUntilLarge = css`
    ${mediaQuery.until(breakpoints.large)} {
        margin-right: -${gutterSizes.large};
    }
`;

export const onlyShowOn = (
    viewportSize: 'LARGE_SCREEN' | 'NON_LARGE_SCREEN' | 'MEDIUM_AND_ABOVE'
) => {
    if (viewportSize === 'LARGE_SCREEN') {
        return hiddenUntilLarge;
    } else if (viewportSize === 'MEDIUM_AND_ABOVE') {
        return hiddenUntilMedium;
    } else {
        return hiddenFromLarge;
    }
};

export const StyledAppContainer = ({
    children,
    isDuckDuckGoBlockingAds,
    ...props
}: React.PropsWithChildren & { isDuckDuckGoBlockingAds: boolean }) => {
    return (
        <Container
            {...qaAttribute('app-container')}
            css={css`
                padding-left: 0;
                padding-right: 0;

                ${mediaQuery.from(breakpoints.xlarge)} {
                    display: grid;
                    grid-template-columns: auto auto 300px;
                    ${isDuckDuckGoBlockingAds
                        ? 'grid-template-rows: auto;'
                        : 'grid-template-rows: 287px auto;'}
                    grid-auto-flow: dense;
                    grid-row-gap: 0;
                    grid-column-gap: ${gutterSizes.xxlarge};
                }
            `}
            {...props}
        >
            {children}
        </Container>
    );
};

export const StyledSectionTopAndLeftAndMiddle = ({
    children,
    ...props
}: React.PropsWithChildren) => {
    return (
        <div
            css={css`
                grid-column: span 2;
                grid-row: span 1;
            `}
            {...qaAttribute('section-top-and-left-and-middle')}
            {...props}
        >
            {children}
        </div>
    );
};

export const StyledSectionTop = ({ children, ...props }: React.PropsWithChildren) => {
    return (
        <div
            {...qaAttribute('section-top')}
            css={css`
                padding: 0 ${gutterSizes.large};
            `}
            {...props}
        >
            {children}
        </div>
    );
};

export const containerPadding = css`
    padding-left: ${gutterSizes.large};
    padding-right: ${gutterSizes.large};

    ${mediaQuery.between(breakpoints.large, breakpoints.xlarge)} {
        padding-left: 0;
        padding-right: 0;
    }
`;

export const StyledTBanner = ({
    children,
    isTBanner728pxWide,
    isTBannerFullBleed,
    ...props
}: React.PropsWithChildren & { isTBanner728pxWide: boolean; isTBannerFullBleed: boolean }) => {
    return (
        <div
            {...qaAttribute('tBanner-container')}
            css={css`
                grid-column: span 2;
                grid-row: span 1;
                margin: ${gutterSizes.large} auto;

                ${mediaQuery.until(breakpoints.large)} {
                    display: none;
                }

                ${isTBanner728pxWide &&
                `
                            background: #f1f1f1;
                        `}

                ${isTBannerFullBleed &&
                `
                            grid-column: span 3;

                            #tBanner {
                                position: absolute;
                                margin-left: auto;
                                margin-right: auto;
                                left: 0;
                                right: 0;
                                text-align: center;
                            }
                        `}
            `}
            {...props}
        >
            {children}
        </div>
    );
};

export const StyledBreadcrumbs = ({
    breadcrumbs,
    ...props
}: React.PropsWithChildren & {
    breadcrumbs: BreadcrumbsState;
}) => {
    return (
        <Breadcrumbs
            css={css`
                ${mediaQuery.until(breakpoints.large)} {
                    margin-top: ${gutterSizes.base};
                }

                .breadcrumbs-link {
                    .link {
                        font-size: ${fontSizes.base};
                        color: ${colors.bark60};
                    }

                    &:last-child {
                        .link {
                            pointer-events: none;
                            color: ${colorVariables.textPrimary};
                        }
                    }
                }
            `}
            {...breadcrumbs}
            {...props}
        />
    );
};

export const StyledFloatingFooter = ({ children, ...props }: React.PropsWithChildren) => {
    return (
        <div
            {...qaAttribute('container-floating-footer')}
            css={css`
                position: fixed;
                bottom: 0;
                z-index: 1;
                width: 100%;
                height: auto;
                border-top: 1px solid ${colorVariables.borderLight};
                background-color: ${colorVariables.backgroundNeutral};

                padding-top: ${gutterSizes.medium};

                ${mediaQuery.from(breakpoints.small)} {
                    display: none;
                }
            `}
            {...props}
        >
            {children}
        </div>
    );
};

export const StyledSectionTop1 = ({ children, ...rest }) => (
    <div css={containerPadding} {...rest}>
        {children}
    </div>
);

export const StyledSectionTop2 = ({ children, ...rest }) => (
    <div
        css={[
            containerPadding,
            css`
                grid-column: span 2;
                padding-top: ${gutterSizes.large};
                padding-bottom: ${gutterSizes.large};
                ${mediaQuery.until(breakpoints.large)} {
                    position: sticky;
                    top: 0;
                    z-index: ${zIndexes.low};
                    background-color: white;
                }
            `,
        ]}
        {...rest}
    >
        {children}
    </div>
);

export const StyledSectionRight = ({
    children,
    isTBannerFullBleed,
    ...props
}: React.PropsWithChildren & { isTBannerFullBleed: boolean }) => {
    return (
        <div
            {...qaAttribute('section-right')}
            css={css`
                grid-column: span 1;
                grid-row: span 2;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                margin-top: ${gutterSizes.large};

                ${isTBannerFullBleed &&
                `
                    grid-row: span 1;
                    margin-top: 0;
                `}

                ${mediaQuery.until(breakpoints.xlarge)} {
                    display: none;
                }
            `}
            {...props}
        >
            {children}
        </div>
    );
};

export const StyledSectionLeftAndMiddle = ({
    children,
    isZeroResults,
    pageLayout,
    ...props
}: React.PropsWithChildren & {
    isZeroResults: boolean;
    pageLayout: PageLayout;
}) => {
    return (
        <div
            {...qaAttribute('section-left-and-middle')}
            css={[
                containerPadding,
                css`
                    ${mediaQuery.until(breakpoints.small)} {
                        ${!isZeroResults && pageLayout === PageLayout.TwoColumns && `padding: 0;`}
                    }

                    padding-top: ${gutterSizes.medium};

                    ${mediaQuery.until(breakpoints.medium)} {
                        overflow: hidden;
                    }

                    ${mediaQuery.from(breakpoints.large)} {
                        display: grid;
                        grid-column-gap: ${gutterSizes.xlarge};
                        grid-template-columns: auto minmax(728px, 1fr);
                    }
                `,
            ]}
            {...props}
        >
            {children}
        </div>
    );
};

export const StyledSectionMiddle = ({ children, ...props }: React.PropsWithChildren) => {
    return (
        <div
            {...qaAttribute('section-middle')}
            css={css`
                align-items: start;
                margin-bottom: ${gutterSizes.xlarge};
                width: 100%;
            `}
            {...props}
        >
            {children}
        </div>
    );
};
